import { FC, useCallback, useMemo } from 'react';
import { useField } from 'formik';

import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MultiSelectChip from 'components/MultiSelectChip';
import MultiSelectMenuItem from 'components/MultiSelectMenuItem';

export type Option = {
  id: string;
  name: string;
  icon?: string | null;
};

type Props = {
  name: string;
  label?: string;
} & Omit<AutocompleteProps<Option, true, false, false>, 'renderInput'>;

export type MultiSelectProps = Omit<Props, 'options'>;

const isOptionEqualToValue = (option: Option, value: Option) =>
  option.id === value.id;

const MultiSelect: FC<Props> = props => {
  const { name, label, placeholder, options, ...rest } = props;
  const [{ value }, , { setValue }] = useField(name);

  const handleChange = useCallback(
    (event, value) => {
      setValue(value);
    },
    [setValue]
  );

  const sorted = useMemo(
    () =>
      [...options].sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      }),
    [options]
  );

  return (
    <Autocomplete
      multiple
      id={props.name}
      getOptionLabel={option => option.name}
      value={value}
      onChange={handleChange}
      isOptionEqualToValue={isOptionEqualToValue}
      options={sorted}
      renderTags={(value, getTagProps) =>
        value.map((tag, index) => (
          <MultiSelectChip
            key={index}
            option={tag}
            getTagProps={getTagProps}
            index={index}
          />
        ))
      }
      renderOption={(props, option, { selected }) => (
        <MultiSelectMenuItem
          {...props}
          name={option.name}
          icon={option.icon}
          selected={selected}
          value={option.id}
        />
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          label={label}
          placeholder={placeholder}
        />
      )}
      {...rest}
    />
  );
};

export default MultiSelect;
